import React, { useState } from 'react';
import {
  FileText,
  Plus,
  Trash2,
  AlertCircle,
  X,
  Clock
} from 'lucide-react';
import { TextField, Autocomplete } from '@anyemp/ui-kit';
import { noteTypes, getNoteTypeLabel } from '../../utils/noteTypeUtils';

const NotesManager = ({
  entityId,
  entityType = 'Talent', // 'Talent' или 'JobApplication'
  notes = [],
  onNotesChange,
  showTitle = true,
  title = "Notes",
  className = "",
  allowAddRemove = true
}) => {
  const [error, setError] = useState(null);

  const addNote = () => {
    const newNote = {
      id: Date.now(), // Временный ID
      note_type: '',
      note: '',
      created_at: new Date().toISOString()
    };

    onNotesChange([...notes, newNote]);
  };

  const removeNote = (index) => {
    onNotesChange(notes.filter((_, i) => i !== index));
  };

  const updateNote = (index, field, value) => {
    const updatedNotes = notes.map((note, i) => {
      if (i === index) {
        return { ...note, [field]: value };
      }
      return note;
    });
    onNotesChange(updatedNotes);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isValidNote = (note) => {
    return note.note_type && note.note && note.note.trim().length > 0;
  };


  return (
    <div className={`bg-light-bg-primary dark:bg-dark-bg-secondary shadow rounded-lg p-6 border border-light-border-primary dark:border-dark-border-primary ${className}`}>
      {showTitle && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h3 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary flex items-center">
            <FileText className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-400" />
            {title}
          </h3>
          {allowAddRemove && (
            <button
              type="button"
              onClick={addNote}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Note
            </button>
          )}
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-red-400 dark:text-red-300 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
              <p className="mt-1 text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
            <button
              type="button"
              onClick={() => setError(null)}
              className="ml-3 inline-flex items-center justify-center h-5 w-5 text-red-400 dark:text-red-300 hover:text-red-600 dark:hover:text-red-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {notes.length === 0 ? (
        <div className="text-center py-8">
          <FileText className="h-12 w-12 text-light-text-tertiary dark:text-dark-text-tertiary mx-auto mb-4" />
          <p className="mt-2 text-sm text-light-text-secondary dark:text-dark-text-secondary">No notes added</p>
          <p className="mt-1 text-sm text-light-text-tertiary dark:text-dark-text-tertiary">Click "Add Note" to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notes.map((note, index) => (
            <div
              key={note.id || index}
              className={`border rounded-lg p-4 transition-colors ${
                isValidNote(note)
                  ? 'border-light-border-primary dark:border-dark-border-primary bg-light-bg-secondary dark:bg-dark-bg-tertiary'
                  : 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/10'
              }`}
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center space-x-2">
                  <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  {note.note_type && (
                    <span className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
                      {getNoteTypeLabel(note.note_type)}
                    </span>
                  )}
                </div>
                {allowAddRemove && (
                  <button
                    type="button"
                    onClick={() => removeNote(index)}
                    className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                    title="Remove note"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Autocomplete
                    fullWidth
                    label="Note Type *"
                    options={noteTypes}
                    value={note.note_type || ''}
                    onChange={(value) => {
                      updateNote(index, 'note_type', value || '');
                    }}
                    placeholder="Select note type"
                  />
                </div>

                {note.created_at && (
                  <div className="flex items-end">
                    <div className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary flex items-center">
                      <Clock className="h-3 w-3 mr-1" />
                      Created: {formatDateTime(note.created_at)}
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-3">
                <TextField
                  label="Note"
                  required
                  multiline
                  rows={4}
                  fullWidth
                  value={note.note || ''}
                  onChange={(e) => updateNote(index, 'note', e.target.value)}
                  placeholder="Enter your note..."
                />
              </div>

              {!isValidNote(note) && (
                <div className="mt-2 text-xs text-red-600 dark:text-red-400 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Note type and note text are required
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {notes.length > 0 && (
        <div className="mt-4 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
          {notes.filter(n => isValidNote(n)).length} valid note(s) of {notes.length} total
        </div>
      )}
    </div>
  );
};

export default NotesManager;

